import { ChangeDetectionStrategy, Component, ElementRef, ViewEncapsulation, computed, inject, input } from '@angular/core';
import { CommonModule } from '@angular/common';

export type CeButtonSize = 'lg' | 'md' | 'sm' | 'xs' | '2xs';

const ATTRIBUTE_SIZE_MAP: Record<string, CeButtonSize> = {
    'ce-button-large': 'lg',
    'ce-button-small': 'sm',
    'ce-button-xs': 'xs',
    'ce-button-2xs': '2xs',
};

// Legacy long-form class names — emitted alongside the short form for external consumers
// (e.g. layout-templates SCSS, dialog-header-actions spec). Remove once those migrate.
const LEGACY_SIZE_CLASS: Partial<Record<CeButtonSize, string>> = {
    md: 'ce-button-default',
    lg: 'ce-button-large',
    sm: 'ce-button-small',
};

// TODO: Think about splitting this into multiple parts for better readability (+ link buttons / anchor buttons)
/* eslint-disable @angular-eslint/component-selector */
@Component({
    selector: `
    button[ce-button], button[ce-button-large], button[ce-button-small], button[ce-button-xs], button[ce-button-2xs], a[ce-button], a[ce-button-large], a[ce-button-small], a[ce-button-xs], a[ce-button-2xs]
  `,
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [CommonModule],
    templateUrl: './ce-button.component.html',
    styleUrl: './ce-button.component.scss',
    host: {
        class: 'ce-button',
        '[class]': 'computedClasses()',
    }
})
/* eslint-enable @angular-eslint/component-selector */
export class CeButtonComponent {
    readonly #elementRef = inject(ElementRef);

    readonly size = input<CeButtonSize | undefined>(undefined);

    readonly #attributeSize: CeButtonSize = this.#detectAttributeSize();
    protected readonly effectiveSize = computed<CeButtonSize>(() => this.size() ?? this.#attributeSize);
    protected readonly computedClasses = computed<string>(() => {
        const size = this.effectiveSize();
        const legacy = LEGACY_SIZE_CLASS[size];
        return legacy ? `ce-button-${size} ${legacy}` : `ce-button-${size}`;
    });

    additionalButtonTypes: string[] = ['ce-button-type-secondary', 'ce-button-type-tertiary', 'ce-button-type-quaternary', 'ce-button-icon', 'ce-button-ghost'];

    constructor() {
        const buttonElement = this.#elementRef.nativeElement as HTMLElement;
        const classList = buttonElement.classList;

        // Loop through the additional button types ('ce-button-type-secondary', 'ce-button-type-tertiary', ...)
        for (const buttonType of this.additionalButtonTypes) {
            if (buttonElement.hasAttribute(buttonType)) {
                classList.add(buttonType);
            }
        }
    }

    #detectAttributeSize(): CeButtonSize {
        const el = this.#elementRef.nativeElement as HTMLElement;
        for (const [attr, size] of Object.entries(ATTRIBUTE_SIZE_MAP)) {
            if (el.hasAttribute(attr)) {
                return size;
            }
        }
        return 'md';
    }
}
